import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { WorkExceptionDialogComponent, WorkExceptionDialogResult } from './work-exception-dialog.component';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';

interface EmployeeOption {
  employeeId: string;
  center?: string | null;
  employeeNumber: string;
  fullName: string;
  firstName?: string | null;
  lastName?: string | null;
  secondLastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companiesRecordId?: string | null;
  orgUnitId?: string | null;
  orgPath?: string | null;
  status?: string | null;
  isActive?: boolean;
}

interface EmployeesResponse {
  data?: EmployeeOption[];
  items?: EmployeeOption[];
  results?: EmployeeOption[];
  employees?: EmployeeOption[];
}
interface CompaniesResponse { items?: CompaniesRecord[]; data?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

interface WorkAssignmentSummary {
  isWorkDay: boolean;
  isRestDay: boolean;
  hasException: boolean;
  journeyName: string | null;
  scheduleName: string | null;
  scheduleMode: string | null;
  expectedStartTime: string | null;
  expectedEndTime: string | null;
  crossesMidnight: boolean;
  expectedWorkMinutes: number | null;
  mealBreakEnabled: boolean;
  mealBreakRequired: boolean;
  mealBreakMode: string | null;
  mealBreakMinutes: number;
  mealBreakStartTime: string | null;
  mealBreakEndTime: string | null;
}

interface WorkAssignmentInfo {
  workAssignmentId: string;
  companiesRecordId: string | null;
  orgUnitId: string | null;
  employeeId: string | null;
  workCycleId: string | null;
  restConfigurationId: string | null;
  cycleStartDate: string | null;
  cycleOffset: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

interface RestConfigurationInfo {
  restConfigurationId?: string;
  name?: string | null;
  mode?: string | null;
  restDays?: number[];
  weekdays?: number[];
  cycleReference?: string | null;
}

interface WorkExceptionInfo {
  workExceptionId?: string;
  reason?: string | null;
  [key: string]: unknown;
}

interface WorkCalendarDay {
  isoWeekday: number;
  assignmentSource: string | null;
  assignment: WorkAssignmentInfo | null;
  workDate: string;
  dayType: string | null;
  cycleDayOrder: number | null;
  anchorDate: string | null;
  restConfiguration: RestConfigurationInfo | null;
  exception: WorkExceptionInfo | null;
  summary: WorkAssignmentSummary;
}

interface WorkCalendarResponse {
  employee: EmployeeOption;
  timezone: string;
  dateFrom: string;
  dateTo: string;
  totalDays: number;
  days: WorkCalendarDay[];
}

interface CalendarCell {
  key: string;
  day: WorkCalendarDay | null;
}

interface CalendarMonthSection {
  key: string;
  label: string;
  dayCount: number;
  weeks: CalendarCell[][];
  cells: CalendarCell[];
}

interface WorkCalendarFilterState {
  selectedCompanyId: string;
  selectedEmployeeId: string;
  employeeSearchText: string;
  dateFrom: string;
  dateTo: string;
}

@Component({
  selector: 'app-work-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    OverlayModule,
    TranslateModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    DateTimePickerFieldComponent,
    LoadingStateComponent,
  ],
  templateUrl: './work-calendar.component.html',
  styleUrl: './work-calendar.component.scss',
})
export class WorkCalendarComponent implements OnInit {
  private static readonly FILTER_STATE_KEY = 'work-calendar:filters';

  employees: EmployeeOption[] = [];
  companies: CompaniesRecord[] = [];
  selectedCompanyId = '';
  selectedEmployeeId = '';
  employeeSearchText: string | EmployeeOption = '';
  dateFrom = '';
  dateTo = '';

  isLoadingEmployees = false;
  isLoadingCalendar = false;
  workCalendar: WorkCalendarResponse | null = null;
  monthSections: CalendarMonthSection[] = [];
  activeMonthIndex = 0;
  hoveredDate: string | null = null;
  private lastFetchSignature = '';
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private dayPreviewHandle: ReturnType<typeof setTimeout> | null = null;

  readonly weekdayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  readonly dayPreviewPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 10 },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -10 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 10 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 10 },
  ];

  private readonly apiService = inject(AuthorizedApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly companySelection = inject(CompanySelectionService);

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.clearDayPreviewTimer());
    this.restoreFilterState();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.monthSections = this.buildMonthSections(this.workCalendar?.days || []);
    });
    void this.initializeData();
  }

  showDayPreview(day: WorkCalendarDay): void {
    this.clearDayPreviewTimer();
    this.dayPreviewHandle = setTimeout(() => {
      this.hoveredDate = day.workDate;
      this.dayPreviewHandle = null;
    }, 220);
  }

  hideDayPreview(day: WorkCalendarDay): void {
    this.clearDayPreviewTimer();
    if (this.hoveredDate === day.workDate) {
      this.hoveredDate = null;
    }
  }

  get selectedEmployee(): EmployeeOption | null {
    return this.employees.find((employee) => employee.employeeId === this.selectedEmployeeId) || this.workCalendar?.employee || null;
  }

  get filteredEmployees(): EmployeeOption[] {
    return this.employees;
  }

  readonly displayEmployeeOption = (value: string | EmployeeOption | null): string => this.getEmployeeSearchText(value);

  get currentMonth(): CalendarMonthSection | null {
    return this.monthSections[this.activeMonthIndex] || null;
  }

  get canGoPrevMonth(): boolean {
    return this.activeMonthIndex > 0;
  }

  get canGoNextMonth(): boolean {
    return this.activeMonthIndex < this.monthSections.length - 1;
  }

  get canSearch(): boolean {
    return Boolean(this.selectedEmployeeId && this.dateFrom && this.dateTo && !this.isLoadingCalendar);
  }

  get hasResult(): boolean {
    return Boolean(this.workCalendar);
  }

  get visibleDays(): WorkCalendarDay[] {
    return (this.currentMonth?.weeks || [])
      .flatMap((week) => week)
      .map((cell) => cell.day)
      .filter((day): day is WorkCalendarDay => Boolean(day));
  }

  get visibleWorkDays(): number {
    return this.visibleDays.filter((day) => day.summary.isWorkDay).length;
  }

  get visibleRestDays(): number {
    return this.visibleDays.filter((day) => day.summary.isRestDay).length;
  }

  get visibleExceptions(): number {
    return this.visibleDays.filter((day) => day.summary.hasException).length;
  }

  async initializeData(): Promise<void> {
    await this.loadCompanies();
    await this.loadEmployees(this.getEmployeeSearchText(this.employeeSearchText));
    this.onFiltersChanged();
  }

  onFiltersChanged(): void {
    if (this.isLoadingEmployees) {
      return;
    }

    this.persistFilterState();

    if (!this.selectedEmployeeId || !this.dateFrom || !this.dateTo) {
      if (!this.selectedEmployeeId) {
        this.workCalendar = null;
        this.monthSections = [];
        this.activeMonthIndex = 0;
        this.lastFetchSignature = '';
      }
      return;
    }

    if (this.validateFilters()) {
      return;
    }

    const signature = this.getFilterSignature();
    if (signature === this.lastFetchSignature && this.workCalendar) {
      return;
    }

    void this.fetchCalendar();
  }

  onEmployeeSearchInput(value: string | EmployeeOption): void {
    const searchText = this.getEmployeeSearchText(value);
    this.employeeSearchText = value;

    const selectedEmployee = this.employees.find((employee) => employee.employeeId === this.selectedEmployeeId);
    if (selectedEmployee && this.getEmployeeSearchText(selectedEmployee) !== searchText.trim()) {
      this.selectedEmployeeId = '';
      this.workCalendar = null;
      this.monthSections = [];
      this.activeMonthIndex = 0;
      this.lastFetchSignature = '';
    }

    this.persistFilterState();

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(searchText);
    }, 250);
  }

  onEmployeeSelected(employee: EmployeeOption | null): void {
    this.selectedEmployeeId = employee?.employeeId || '';
    this.employeeSearchText = employee || '';
    this.persistFilterState();
    this.onFiltersChanged();
  }

  onCompanyChanged(companyId: string): void {
    this.selectedCompanyId = companyId || '';
    this.selectedEmployeeId = '';
    this.employeeSearchText = '';
    this.workCalendar = null;
    this.monthSections = [];
    this.lastFetchSignature = '';
    this.companySelection.rememberSelection(this.selectedCompanyId);
    this.persistFilterState();
    void this.loadEmployees();
  }

  async loadEmployees(search = ''): Promise<void> {
    this.isLoadingEmployees = true;
    try {
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === this.selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeOption[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(this.selectedCompanyId ? { companiesRecordId: this.selectedCompanyId } : {}), ...(search ? { search } : {}) },
        })
      );

      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      if (this.selectedEmployeeId) {
        const refreshedSelectedEmployee = this.employees.find((employee) => employee.employeeId === this.selectedEmployeeId);
        this.employeeSearchText = refreshedSelectedEmployee || this.getEmployeeSearchText(this.employeeSearchText);
      }
    } catch (error) {
      console.error('Error loading employees for work calendar', error);
      this.employees = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workCalendarCatalog.notifications.loadEmployeesError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoadingEmployees = false;
    }
  }

  private async loadCompanies(): Promise<void> {
    const previousCompanyId = this.selectedCompanyId;
    const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
    this.companies = (Array.isArray(response) ? response : response.items || response.data || response.results || response.companiesRecords || [])
      .filter((company) => company.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
    this.selectedCompanyId = this.companySelection.resolveSharedSelection(this.companies, this.selectedCompanyId);
    if (previousCompanyId && previousCompanyId !== this.selectedCompanyId) {
      this.selectedEmployeeId = '';
      this.employeeSearchText = '';
    }
  }

  async fetchCalendar(): Promise<void> {
    const validationError = this.validateFilters();
    if (validationError) {
      this.showNotification('snackbar-danger', this.t(validationError), 'bottom', 'center');
      return;
    }

    this.isLoadingCalendar = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<WorkCalendarResponse>(`employees/${this.selectedEmployeeId}/work-config-range`, {
          params: {
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
          },
        })
      );

      this.workCalendar = {
        ...response,
        days: [...(response.days || [])].sort((left, right) => left.workDate.localeCompare(right.workDate)),
      };
      this.monthSections = this.buildMonthSections(this.workCalendar.days);
      this.activeMonthIndex = 0;
      this.lastFetchSignature = this.getFilterSignature();
    } catch (error) {
      console.error('Error loading work calendar', error);
      this.workCalendar = null;
      this.monthSections = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workCalendarCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoadingCalendar = false;
    }
  }

  clearFilters(): void {
    this.selectedCompanyId = this.companies.length === 1 ? this.companies[0].companiesRecordId : '';
    this.companySelection.rememberSelection(this.selectedCompanyId);
    this.selectedEmployeeId = '';
    this.employeeSearchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.workCalendar = null;
    this.monthSections = [];
    this.activeMonthIndex = 0;
    this.lastFetchSignature = '';
    this.clearPersistedFilterState();
  }

  goToPreviousMonth(): void {
    if (!this.canGoPrevMonth) {
      return;
    }
    this.activeMonthIndex -= 1;
    this.syncSelectedDayWithActiveMonth();
  }

  goToNextMonth(): void {
    if (!this.canGoNextMonth) {
      return;
    }
    this.activeMonthIndex += 1;
    this.syncSelectedDayWithActiveMonth();
  }

  goToToday(): void {
    const today = this.toDateInputValue(new Date());
    const monthIndex = this.monthSections.findIndex((month) =>
      month.weeks.some((week) => week.some((cell) => cell.day?.workDate === today))
    );
    if (monthIndex >= 0) {
      this.activeMonthIndex = monthIndex;
      this.syncSelectedDayWithActiveMonth();
    }
  }

  getWeekdayDateLabel(isoWeekday: number): string {
    const day = this.visibleDays.find((candidate) => candidate.isoWeekday === isoWeekday);
    if (!day) {
      return '';
    }
    return new Intl.DateTimeFormat(this.currentLocale, { day: 'numeric', month: 'short' })
      .format(this.parseDate(day.workDate))
      .replace('.', '');
  }

  isWeekdayToday(isoWeekday: number): boolean {
    const today = this.toDateInputValue(new Date());
    return this.visibleDays.some((day) => day.isoWeekday === isoWeekday && day.workDate === today);
  }

  getEmployeeLabel(employee: EmployeeOption): string {
    const segments = [employee.fullName, employee.employeeNumber].filter(Boolean);
    return segments.join(' · ');
  }

  private getEmployeeSearchText(value: string | EmployeeOption | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return this.getEmployeeLabel(value);
  }

  getDayNumber(workDate: string): string {
    return workDate.split('-')[2] || '';
  }

  getDayTypeLabel(day: WorkCalendarDay): string {
    if (day.summary.isRestDay) {
      return this.t('workCalendarCatalog.dayTypes.rest');
    }

    if (day.summary.isWorkDay) {
      return this.t('workCalendarCatalog.dayTypes.work');
    }

    return this.t('workCalendarCatalog.dayTypes.unresolved');
  }

  getDayTone(day: WorkCalendarDay): string {
    if (day.summary.hasException) {
      return 'exception';
    }

    if (day.summary.isRestDay) {
      return 'rest';
    }

    if (day.summary.isWorkDay) {
      return 'work';
    }

    return 'neutral';
  }

  getScheduleLabel(day: WorkCalendarDay): string {
    if (day.summary.isRestDay) {
      return this.t('workCalendarCatalog.cards.restLabel');
    }

    return day.summary.scheduleName || day.summary.journeyName || this.t('workCalendarCatalog.cards.noSchedule');
  }

  getTimeRangeLabel(day: WorkCalendarDay): string {
    const start = day.summary.expectedStartTime;
    const end = day.summary.expectedEndTime;
    if (!start || !end) {
      return this.t('workCalendarCatalog.cards.noTime');
    }

    return `${start} - ${end}`;
  }

  getExpectedHoursLabel(day: WorkCalendarDay): string {
    const minutes = day.summary.expectedWorkMinutes;
    if (!minutes) {
      return this.t('workCalendarCatalog.cards.noHours');
    }

    const hours = minutes / 60;
    return Number.isInteger(hours) ? `${hours} h` : `${hours.toFixed(1)} h`;
  }

  getMealBreakLabel(day: WorkCalendarDay): string {
    if (!day.summary.mealBreakEnabled) {
      return this.t('workCalendarCatalog.cards.noMealBreak');
    }

    if (day.summary.mealBreakStartTime && day.summary.mealBreakEndTime) {
      return `${day.summary.mealBreakStartTime} - ${day.summary.mealBreakEndTime}`;
    }

    if (day.summary.mealBreakMinutes) {
      return this.t('workCalendarCatalog.cards.mealBreakMinutes', { minutes: day.summary.mealBreakMinutes });
    }

    return this.t('workCalendarCatalog.cards.mealBreakEnabled');
  }

  getAssignmentSourceLabel(source: string | null): string {
    const key = source || 'none';
    return this.t(`workCalendarCatalog.assignmentSources.${key}`);
  }

  openWorkException(day: WorkCalendarDay): void {
    const companyId =
      day.assignment?.companiesRecordId ||
      this.selectedEmployee?.companiesRecordId ||
      this.workCalendar?.employee?.companiesRecordId ||
      '';

    const dialogRef = this.dialog.open(WorkExceptionDialogComponent, {
      width: '720px',
      maxWidth: '94vw',
      autoFocus: false,
      data: {
        workExceptionId: typeof day.exception?.workExceptionId === 'string' ? day.exception.workExceptionId : '',
        companiesRecordId: companyId,
        employeeId: day.assignment?.employeeId || this.selectedEmployeeId,
        employeeLabel: this.selectedEmployee ? this.getEmployeeLabel(this.selectedEmployee) : '-',
        date: day.workDate,
        currentDayType: day.summary.isRestDay ? 'rest' : day.summary.isWorkDay ? 'work' : 'neutral',
        currentScheduleLabel: this.getScheduleLabel(day),
      },
    });

    dialogRef.afterClosed().subscribe((result?: WorkExceptionDialogResult) => {
      if (result?.refresh) {
        void this.fetchCalendar();
      }
    });
  }

  formatLongDate(date: string | null | undefined): string {
    if (!date) {
      return '-';
    }

    return new Intl.DateTimeFormat(this.translate.currentLang === 'en' || this.translate.currentLang === 'us' ? 'en-US' : 'es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(this.parseDate(date));
  }

  formatShortDate(date: string | null | undefined): string {
    if (!date) {
      return '-';
    }

    return new Intl.DateTimeFormat(this.translate.currentLang === 'en' || this.translate.currentLang === 'us' ? 'en-US' : 'es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(this.parseDate(date));
  }

  trackByMonth(_index: number, month: CalendarMonthSection): string {
    return month.key;
  }

  trackByCell(_index: number, cell: CalendarCell): string {
    return cell.key;
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  toPickerDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  fromPickerDate(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDateFromSelected(event: MatDatepickerInputEvent<Date>): void {
    this.dateFrom = this.fromPickerDate(event.value);
    this.persistFilterState();
    this.onFiltersChanged();
  }

  onDateToSelected(event: MatDatepickerInputEvent<Date>): void {
    this.dateTo = this.fromPickerDate(event.value);
    this.persistFilterState();
    this.onFiltersChanged();
  }

  private validateFilters(): string | null {
    if (!this.selectedEmployeeId) {
      return 'workCalendarCatalog.notifications.employeeRequired';
    }

    if (!this.dateFrom || !this.dateTo) {
      return 'workCalendarCatalog.notifications.rangeRequired';
    }

    if (this.dateFrom > this.dateTo) {
      return 'workCalendarCatalog.notifications.invalidRange';
    }

    const diffDays = Math.floor((this.parseDate(this.dateTo).getTime() - this.parseDate(this.dateFrom).getTime()) / 86400000) + 1;
    if (diffDays > 31) {
      return 'workCalendarCatalog.notifications.rangeExceeded';
    }

    return null;
  }

  private extractEmployees(response: EmployeeOption[] | EmployeesResponse | null | undefined): EmployeeOption[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (!response) {
      return [];
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.items)) {
      return response.items;
    }
    if (Array.isArray(response.results)) {
      return response.results;
    }
    if (Array.isArray(response.employees)) {
      return response.employees;
    }
    return [];
  }

  private buildMonthSections(days: WorkCalendarDay[]): CalendarMonthSection[] {
    if (!days.length) {
      return [];
    }

    const grouped = new Map<string, WorkCalendarDay[]>();
    for (const day of days) {
      const key = day.workDate.slice(0, 7);
      const monthDays = grouped.get(key) || [];
      monthDays.push(day);
      grouped.set(key, monthDays);
    }

    return Array.from(grouped.entries()).map(([key, monthDays]) => {
      const sortedDays = [...monthDays].sort((left, right) => left.workDate.localeCompare(right.workDate));
      const first = sortedDays[0];
      const leading = Math.max((first.isoWeekday || 1) - 1, 0);
      const cells: CalendarCell[] = [];

      for (let index = 0; index < leading; index += 1) {
        cells.push({ key: `${key}-leading-${index}`, day: null });
      }

      for (const day of sortedDays) {
        cells.push({ key: day.workDate, day });
      }

      const remainder = cells.length % 7;
      if (remainder > 0) {
        const trailing = 7 - remainder;
        for (let index = 0; index < trailing; index += 1) {
          cells.push({ key: `${key}-trailing-${index}`, day: null });
        }
      }

      const weeks: CalendarCell[][] = [];
      for (let index = 0; index < cells.length; index += 7) {
        weeks.push(cells.slice(index, index + 7));
      }

      return {
        key,
        label: this.formatMonthLabel(key),
        dayCount: sortedDays.length,
        weeks,
        cells,
      };
    });
  }

  private formatMonthLabel(key: string): string {
    const [year, month] = key.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, 1);
    return new Intl.DateTimeFormat(this.translate.currentLang === 'en' || this.translate.currentLang === 'us' ? 'en-US' : 'es-MX', {
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private get currentLocale(): string {
    const language = (this.translate.currentLang || this.translate.defaultLang || 'es').toLowerCase();
    return language === 'en' || language === 'us' ? 'en-US' : 'es-MX';
  }

  private clearDayPreviewTimer(): void {
    if (this.dayPreviewHandle) {
      clearTimeout(this.dayPreviewHandle);
      this.dayPreviewHandle = null;
    }
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDate(value: string): Date {
    return new Date(`${value}T12:00:00`);
  }

  private restoreFilterState(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }

    try {
      const rawState = sessionStorage.getItem(WorkCalendarComponent.FILTER_STATE_KEY);
      if (!rawState) {
        return false;
      }

      const state = JSON.parse(rawState) as Partial<WorkCalendarFilterState>;
      this.selectedCompanyId = typeof state.selectedCompanyId === 'string' ? state.selectedCompanyId : '';
      this.selectedEmployeeId = typeof state.selectedEmployeeId === 'string' ? state.selectedEmployeeId : '';
      this.employeeSearchText = typeof state.employeeSearchText === 'string' ? state.employeeSearchText : '';
      this.dateFrom = typeof state.dateFrom === 'string' ? state.dateFrom : '';
      this.dateTo = typeof state.dateTo === 'string' ? state.dateTo : '';

      return true;
    } catch {
      this.clearPersistedFilterState();
      return false;
    }
  }

  private persistFilterState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const state: WorkCalendarFilterState = {
      selectedCompanyId: this.selectedCompanyId,
      selectedEmployeeId: this.selectedEmployeeId,
      employeeSearchText: this.getEmployeeSearchText(this.employeeSearchText),
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    };
    sessionStorage.setItem(WorkCalendarComponent.FILTER_STATE_KEY, JSON.stringify(state));
  }

  private clearPersistedFilterState(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(WorkCalendarComponent.FILTER_STATE_KEY);
    }
  }

  private syncSelectedDayWithActiveMonth(): void {
    return;
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 3200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private getFilterSignature(): string {
    return `${this.selectedCompanyId}|${this.selectedEmployeeId}|${this.dateFrom}|${this.dateTo}`;
  }
}
